/**
 * Quick Add Drawer Controller
 * Features:
 *  - AJAX product fetch via Section Rendering API
 *  - Metal picker with validation (red border highlight)
 *  - Size validation
 *  - AJAX Add to Cart (no redirect), opens the mini cart on success
 *  - Wishlist toggle (localStorage)
 *  - Esc key + overlay click to close
 */

(function() {
  const drawer   = document.getElementById('quickAddDrawer');
  const overlay  = document.getElementById('quickAddOverlay');
  const content  = document.getElementById('quickAddContent');
  const closeBtn = document.getElementById('quickAddClose');
  const WISHLIST_KEY = 'unplated_wishlist';

  if (!drawer || !content || !overlay) return;

  // ── OPEN ──────────────────────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.p-add-v2');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.handle;
    if (!handle) return;
    // Carry over whatever metal/stone is currently active on the product card
    // (collection.js swatch state), so Quick Add remembers the card's selection.
    const productId = btn.dataset.quickAdd;
    const state = productId && window.unplatedCardVariantState ? window.unplatedCardVariantState[productId] : null;
    openQuickAdd(handle, state && state.metal, state && state.stone);
  });

  // Exposed so collection.js's "+N" stone overflow control can open this same
  // drawer (rather than building a second picker UI), pre-selecting the card's state.
  window.unplatedOpenQuickAdd = openQuickAdd;

  async function openQuickAdd(handle, presetMetal, presetStone) {
    if (window.unplatedCloseOverlays) window.unplatedCloseOverlays('quickadd');
    overlay.classList.add('active');
    drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    content.innerHTML = `
      <div class="q-add-loading">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="28" height="28" style="animation: spin 1s linear infinite; opacity:0.4;">
          <path stroke-linecap="round" d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        Preparing your selection&hellip;
      </div>`;

    try {
      const res = await fetch(`/products/${handle}?section_id=quick-add-drawer`);
      if (!res.ok) throw new Error('Failed to load');
      content.innerHTML = await res.text();
      initQuickAddForm(handle, presetMetal, presetStone);
    } catch (err) {
      content.innerHTML = `<div class="q-add-error">Sorry, we couldn't load this item.<br><small>Please try again.</small></div>`;
      console.error('[QuickAdd]', err);
    }
  }

  // ── CLOSE ─────────────────────────────────────────────────────────────────
  function closeQuickAdd() {
    drawer.classList.remove('active');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  if (closeBtn) closeBtn.addEventListener('click', closeQuickAdd);
  overlay.addEventListener('click', closeQuickAdd);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) closeQuickAdd();
  });
  document.addEventListener('unplated:closeOverlays', (e) => {
    if (e.detail.except !== 'quickadd' && drawer.classList.contains('active')) closeQuickAdd();
  });

  // ── FORM INIT ─────────────────────────────────────────────────────────────
  function initQuickAddForm(handle, presetMetal, presetStone) {
    const form = document.getElementById('q-add-form');
    if (!form) return;

    const noVariants = form.dataset.noVariants === 'true';

    // Selector groups (each may or may not be in the DOM)
    const sizeGroup      = document.getElementById('qSizeGroup');
    const sizeSelect     = document.getElementById('qSizeSelect');
    const sizeError      = document.getElementById('qSizeError');
    const braceletGroup  = document.getElementById('qBraceletGroup');
    const braceletSelect = document.getElementById('qBraceletSelect');
    const braceletError  = document.getElementById('qBraceletError');
    const chainGroup     = document.getElementById('qChainGroup');
    const chainSelect    = document.getElementById('qChainSelect');
    const chainError     = document.getElementById('qChainError');
    const stoneGroup     = document.getElementById('qStoneGroup');
    const stoneSelect    = document.getElementById('qStoneSelect');
    const stoneError     = document.getElementById('qStoneError');
    const charmGroup     = document.getElementById('qCharmGroup');
    const charmInput     = document.getElementById('qCharmInput');
    const charmError     = document.getElementById('qCharmError');
    const engravingGroup = document.getElementById('qEngravingGroup');
    const engravingInput = document.getElementById('qEngravingInput');
    const engravingError = document.getElementById('qEngravingError');

    const wishBtn        = document.getElementById('qWishlistBtn');
    const wishLabel      = wishBtn ? wishBtn.querySelector('.wish-label') : null;
    const variantData    = JSON.parse(document.getElementById('qVariantData')?.textContent || '[]');
    const variantIdInput = document.getElementById('qAddVariantId');
    const optionSelects  = form.querySelectorAll('.variant-option-select');
    const priceEl        = document.getElementById('qAddPrice');

    // ── Swatch button handlers — update hidden select + trigger updateVariant ──

    // Metal swatches
    const goldBtns     = document.getElementById('qGoldBtns');
    const goldSelect   = document.getElementById('qGoldVariantSelect');
    if (goldBtns && goldSelect) {
      goldBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.gold-btn');
        if (!btn) return;
        goldBtns.querySelectorAll('.gold-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        goldSelect.value = btn.dataset.gold;
        goldSelect.dispatchEvent(new Event('change'));
      });
    }

    // Colourstone swatches
    const colourstoneBtns   = document.getElementById('qColourstoneBtns');
    const colourstoneSelect = document.getElementById('qColourstoneVariantSelect');
    if (colourstoneBtns && colourstoneSelect) {
      colourstoneBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.stone-btn');
        if (!btn) return;
        colourstoneBtns.querySelectorAll('.stone-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        colourstoneSelect.value = btn.dataset.colour;
        colourstoneSelect.dispatchEvent(new Event('change'));
      });
    }

    // Closure buttons — also filters Post Length to what's actually stocked for the
    // active closure (e.g. Butterfly Back never offers 6mm if that variant doesn't exist).
    const closureBtns   = document.getElementById('qClosureBtns');
    const closureSelect = document.getElementById('qClosureVariantSelect');
    const postLengthBtns   = document.getElementById('qPostLengthBtns');
    const postLengthSelect = document.getElementById('qPostLengthVariantSelect');
    const closurePostLengthMetaEl = document.getElementById('qClosurePostLengthMeta');
    let closurePostLengthMeta = null;
    if (closurePostLengthMetaEl) {
      try { closurePostLengthMeta = JSON.parse(closurePostLengthMetaEl.textContent); } catch (err) { closurePostLengthMeta = null; }
    }

    function normalizeLength(s) { return (s || '').replace(/\s+/g, '').toLowerCase(); }

    function filterPostLengths(closureValue) {
      if (!closurePostLengthMeta || !postLengthBtns || !postLengthSelect) return;
      const valid = new Set(
        variantData
          .filter(v => v.options[closurePostLengthMeta.closureIndex] === closureValue)
          .map(v => normalizeLength(v.options[closurePostLengthMeta.postLengthIndex]))
      );
      // Hard rule, independent of whatever variants happen to exist in Shopify —
      // see the matching guard in product.liquid's filterPostLengths.
      if (/butterfly/i.test(closureValue || '')) valid.delete('6mm');
      const btnEls = postLengthBtns.querySelectorAll('[data-postlength]');
      let activeStillValid = false;
      btnEls.forEach(b => {
        const ok = valid.has(normalizeLength(b.dataset.postlength));
        b.style.display = ok ? '' : 'none';
        if (ok && b.classList.contains('active')) activeStillValid = true;
      });
      if (!activeStillValid) {
        const firstValid = postLengthBtns.querySelector('[data-postlength]:not([style*="display: none"])');
        if (firstValid) {
          btnEls.forEach(b => { b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); });
          firstValid.classList.add('active');
          firstValid.setAttribute('aria-pressed', 'true');
          postLengthSelect.value = firstValid.dataset.postlength;
          postLengthSelect.dispatchEvent(new Event('change'));
        }
      }
      // Keep the "Not sure which to pick?" explainer in sync — swap to the guidance
      // written for this closure, then hide any length that isn't selectable for it.
      const accBody = document.getElementById('qPostLengthAccBody');
      if (accBody) {
        const wantButterfly = /butterfly/i.test(closureValue || '');
        accBody.querySelectorAll('[data-closure-group]').forEach(group => {
          const isButterflyGroup = group.dataset.closureGroup === 'butterfly';
          const show = wantButterfly === isButterflyGroup;
          group.style.display = show ? '' : 'none';
          if (show) {
            group.querySelectorAll('[data-length-card]').forEach(card => {
              card.style.display = valid.has(normalizeLength(card.dataset.lengthCard)) ? '' : 'none';
            });
          }
        });
      }
    }

    if (closureBtns && closureSelect) {
      closureBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.closure-btn');
        if (!btn) return;
        closureBtns.querySelectorAll('.closure-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        closureSelect.value = btn.dataset.closure;
        closureSelect.dispatchEvent(new Event('change'));
        filterPostLengths(btn.dataset.closure);
      });
      if (closurePostLengthMeta) {
        const activeClosureBtn = closureBtns.querySelector('.closure-btn.active');
        if (activeClosureBtn) filterPostLengths(activeClosureBtn.dataset.closure);
      }
    }

    // Post Length accordion toggle
    const postLengthAccBtn  = document.getElementById('qPostLengthAccBtn');
    const postLengthAccBody = document.getElementById('qPostLengthAccBody');
    if (postLengthAccBtn && postLengthAccBody) {
      postLengthAccBtn.addEventListener('click', () => {
        const open = postLengthAccBody.classList.toggle('open');
        postLengthAccBtn.setAttribute('aria-expanded', String(open));
      });
    }

    // Post Length buttons
    if (postLengthBtns && postLengthSelect) {
      postLengthBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-postlength]');
        if (!btn) return;
        postLengthBtns.querySelectorAll('[data-postlength]').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        postLengthSelect.value = btn.dataset.postlength;
        postLengthSelect.dispatchEvent(new Event('change'));
      });
    }

    // Pre-select the metal/stone that was last active on the product card (if any),
    // so Quick Add "remembers" the card's swatch selection instead of always
    // defaulting to the first option.
    if (presetMetal && goldBtns) {
      const match = goldBtns.querySelector(`.gold-btn[data-gold="${CSS.escape(presetMetal)}"]`);
      if (match) match.click();
    }
    if (presetStone && colourstoneBtns) {
      const match = colourstoneBtns.querySelector(`.stone-btn[data-colour="${CSS.escape(presetStone)}"]`);
      if (match) match.click();
    }

    // Helper: attach clear-error listener to a select/input
    function clearOnChange(el, group, errEl) {
      if (!el) return;
      el.addEventListener(el.tagName === 'INPUT' ? 'input' : 'change', () => {
        if (errEl) errEl.classList.remove('visible');
        if (group) group.classList.remove('has-error');
      });
    }
    clearOnChange(sizeSelect,     sizeGroup,     sizeError);
    clearOnChange(braceletSelect, braceletGroup, braceletError);
    clearOnChange(chainSelect,    chainGroup,    chainError);
    clearOnChange(stoneSelect,    stoneGroup,    stoneError);
    clearOnChange(charmInput,     charmGroup,    charmError);
    clearOnChange(engravingInput, engravingGroup, engravingError);

    // Variant switching — set-contains: every selected option value must appear
    // somewhere in the variant's options array. Order-independent.
    function updateVariant() {
      const vals = Array.from(optionSelects).map(s => s.value).filter(Boolean);
      const matched = variantData.find(v => vals.every(sv => v.options.includes(sv)));

      if (matched) {
        variantIdInput.value = matched.id;
        if (priceEl) priceEl.textContent = formatMoney(matched.price);
        const addBtn = form.querySelector('[type="submit"]');
        if (addBtn) addBtn.disabled = !matched.available;
        if (addBtn) addBtn.textContent = matched.available ? 'Add to order' : 'Sold out';
      } else {
        variantIdInput.value = '';
      }
    }
    optionSelects.forEach(s => s.addEventListener('change', updateVariant));

    // Run once on drawer open so variant ID reflects the pre-selected state
    // (goldInput starts empty until user clicks a swatch, so this also handles
    // products where the server pre-fill is the correct default)
    updateVariant();

    // Wishlist ─────────────────────────────────────────────────────────────
    if (wishBtn) {
      let list = [];
      try { list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (_) {}
      if (list.includes(handle)) {
        wishBtn.classList.add('saved');
        if (wishLabel) wishLabel.textContent = 'Saved in pantry';
      }
      wishBtn.addEventListener('click', () => {
        let cur = [];
        try { cur = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (_) {}
        const idx = cur.indexOf(handle);
        if (idx > -1) {
          cur.splice(idx, 1);
          wishBtn.classList.remove('saved');
          if (wishLabel) wishLabel.textContent = 'Save for later';
        } else {
          cur.push(handle);
          wishBtn.classList.add('saved');
          if (wishLabel) wishLabel.textContent = 'Saved in pantry';
        }
        try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(cur)); } catch (_) {}
      });
    }

    // Helper: show error on a group, return true (for hasError chaining)
    function flagError(group, errEl, isFirst) {
      if (errEl) errEl.classList.add('visible');
      if (group) {
        group.classList.add('has-error');
        if (isFirst) group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return true;
    }

    // Submit ───────────────────────────────────────────────────────────────
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // No-variant products skip all validation — straight to cart
      if (!noVariants) {
        let hasError = false;

        // Ring size
        if (sizeSelect && !sizeSelect.value)         hasError = flagError(sizeGroup,     sizeError,     !hasError);
        // Bracelet size
        if (braceletSelect && !braceletSelect.value) hasError = flagError(braceletGroup, braceletError, !hasError);
        // Chain length
        if (chainSelect && !chainSelect.value)       hasError = flagError(chainGroup,    chainError,    !hasError);
        // Stone (tag-based property selector)
        if (stoneSelect && !stoneSelect.value)       hasError = flagError(stoneGroup,    stoneError,    !hasError);
        // Charm
        if (charmInput) {
          const val = charmInput.value.trim();
          if (!val || !/^[A-Za-z0-9]$/.test(val)) hasError = flagError(charmGroup, charmError, !hasError);
        }
        // Personalisation
        if (engravingInput && !engravingInput.value.trim()) hasError = flagError(engravingGroup, engravingError, !hasError);

        // Belt-and-suspenders: block submission if the selected combination didn't
        // resolve to a real Shopify variant (updateVariant() leaves variantIdInput
        // blank when nothing matches). Picker-level filtering should make this
        // unreachable in normal use, but this catches any edge case cleanly.
        const comboErrorEl = document.getElementById('qClosurePostLengthError');
        if (comboErrorEl) comboErrorEl.classList.remove('visible');
        if (!hasError && variantIdInput && !variantIdInput.value) {
          hasError = true;
          if (comboErrorEl) {
            comboErrorEl.classList.add('visible');
            comboErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }

        if (hasError) return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
        if (!res.ok) throw new Error('Cart error');

        const data = await res.json();

        // ── META PIXEL: AddToCart ──────────────────────────────
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_ids:  [String(data.variant_id || data.id)],
            content_type: 'product',
            content_name: data.product_title || data.title,
            value:        (data.final_price || data.price) / 100,
            currency:     'INR'
          });
        }

        submitBtn.textContent = 'Added ✓';
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Add to order';
          closeQuickAdd();
          if (window.unplatedOpenMiniCart) window.unplatedOpenMiniCart();
        }, 700);

      } catch (err) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Error — Try Again';
        console.error('[QuickAdd Cart]', err);
      }
    });
  }

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  // ── SPINNER KEYFRAME (injected once) ─────────────────────────────────────
  if (!document.getElementById('qa-spin-style')) {
    const s = document.createElement('style');
    s.id = 'qa-spin-style';
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
  }

})();
